/**
 * @Description 折线图
 */
import React, { useRef, useEffect, useState } from 'react';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { SVGRenderer } from 'echarts/renderers';
import { GridComponent, TooltipComponent, TitleComponent, LegendComponent } from 'echarts/components';
// 标签自动布局、全局过渡动画等特性
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { percentValueFormatter } from '@lhb/func';
import { EChartsOption } from 'echarts';
import styles from '@/common/components/Charts/V2PieChart/index.module.less';
import { unstable_batchedUpdates } from 'react-dom';
import cs from 'classnames';
import { themeColor } from '@/common/components/config-v2';

echarts.use([
  SVGRenderer,
  PieChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  LabelLayout,
  UniversalTransition,
]);

export interface V2PieChartProps {
  /**
   * @description 图表标题
   */
  title?: string;
  /**
   * @description 基础类型或者环形
   * @default base
   */
  type?: 'base' | 'circle' | 'rose',
  /**
   * @description 图表主题
   */
  theme?: 'blue' | 'green' | 'purple';
  /**
   * @description 图表宽度，默认不指定，跟随外容器
   */
  width?: number | string;
  /**
   * @description 图表高度
   * @default 270px
   */
  height?: number | string;
  /**
   * @description y轴数据 更多配置见 https://echarts.apache.org/zh/option.html#series-pie
   */
  seriesData: any[];
  /**
   * @description echarts更多配置 详情见https://echarts.apache.org/zh/option.html#title
   * @type EChartsOption
   */
  config?: EChartsOption;
  /**
   * @description 图表外层包装容器的类名
   */
  wrapperClassName?: string;
}

/**
* @description 便捷文档地址
* @see https://reactmobile.lanhanba.net/charts/v2-pie-chart
*/

const PPTPieChart: React.FC<V2PieChartProps> = (props) => {
  const {
    title,
    theme = 'blue',
    type = 'base',
    width,
    height = 270,
    seriesData = [],
    config = {},
    wrapperClassName,
  } = props;
  const chartRef = useRef<any>(null);
  let chart: any;

  const seriesBaseConfig = {
    type: 'pie',
    radius: type === 'circle' ? ['80%', '100%'] : '50%',
    center: [140, '50%'],
    roseType: type === 'rose' ? 'area' : false,
    itemStyle: {
      borderRadius: 3,
      borderColor: '#fff',
      borderWidth: 1,
    },
    label: {
      show: false,
    },
    labelLine: {
      show: false,
    },
    tooltip: {
      position: 'inside',
    },
  };

  const [isConfig, setIsConfig] = useState(false); // options 是否配置完成
  const [options, setOptions] = useState<any>({
    title: {
      show: !!title,
      text: title || '',
      textStyle: {
        color: '#222222',
        fontWeight: 500,
        fontFamily: 'PingFangSC, PingFang SC',
        fontSize: 24,
        lineHeight: 33,
        width: 96,
        overflow: 'break',
        textAlign: 'center'
      },
      top: 'center',
      left: 90,
      ...(config.title || {}),
    },
    legend: {
      type: 'plain',
      width: '45%',
      orient: 'vertical',
      itemWidth: 14,
      itemHeight: 14,
      itemGap: 24,
      itemStyle: {
        borderWidth: 0,
      },
      textStyle: {
        fontSize: 20,
        fontWeight: 400,
        color: '#666666',
        lineHeight: 28,
        rich: {
          a: {
            verticalAlign: 'middle',
          },
        },
        padding: [0, 4, 1, 0],
      },
      icon: 'roundRect',
      top: 'center',
      left: '316px',
      ...(config.legend || {}),
    },
    color: themeColor[theme],
    tooltip: {
      trigger: 'item',
      backgroundColor: '#222',
      borderColor: '#222',
      borderWidth: 0,
      textStyle: {
        color: '#fff',
        fontWeight: 'normal',
        fontSize: 12,
        fontFamily: 'PingFangSC-Regular, PingFang SC',
      },
      confine: true,
      ...(config.tooltip || {}),
    },
    series: [],
  });

  useEffect(() => {
    if (seriesData?.length) {
      const totalValue = seriesData[0]?.data?.reduce((prevVal: number, currVal: any) => {
        return prevVal + currVal.value;
      }, 0); // 饼图的value总和，用于计算每块图例百分比
      unstable_batchedUpdates(() => {
        setOptions({
          ...options,
          series: seriesData.map((item) => {
            return {
              ...seriesBaseConfig,
              ...item,
            };
          }),
          legend: {
            ...options.legend,
            formatter: (name: string) => {
              const data = seriesData[0]?.data || [];
              for (let i = 0; i < data.length; i++) {
                if (data[i].name === name) {
                  const percent = percentValueFormatter(data[i].value, totalValue);
                  return `${name} ${percent}%`;
                }
              }
              return name;
            },
          },
        });
        setIsConfig(true);
      });

    }
  }, [seriesData]);

  useEffect(() => {
    if (!isConfig) return;
    if (chartRef.current) {
      chart = echarts.init(chartRef.current, 'light', {
        renderer: 'svg',
        height,
      });
      chart.setOption(options);
    }
    return () => chart?.dispose();
  }, [options, isConfig]);

  return (
    <div className={cs(styles.V2PieChartWrap, wrapperClassName)} style={{ width, height }}>
      <div ref={chartRef} className={styles.V2PieChart}></div>
    </div>
  );
};

export default PPTPieChart;
